import { Card, CardBody, Col, Container, Row, Button } from "reactstrap";
import React, { useEffect, useState, useMemo } from "react";

import API from "../../utils/API";
import AdminSelect from "../../components/formfields/AdminSelect";
import CSVDownload from "../../components/CsvDownload";
import DateRange from "../../components/formfields/DateRange";
import LeadSourceSelect from "../../components/formfields/LeadSourceSelect";
import Loader from "../../components/Loader";
import MoneyPub from "../../components/fields/MoneyPub";
import MoneyTotal from "../../components/fields/MoneyTotal";
import ProductMulti from "../../components/formfields/ProductMulti";
import Publisher from "../../components/fields/Publisher";
import RCID from "../../components/fields/RCID";
import PublisherSelect from "../../components/formfields/PublisherSelect";
import SortableTable from "../../components/SortableTable";
import dateFormat from "dateformat";
import { parseFilterValues } from "../../utils/helpers/filterHelper";
import { useHistory } from "react-router-dom";

const SummaryReport = () => {
  const history = useHistory();
  const defaultDate =
      dateFormat(new Date().setDate(new Date().getDate() - 6), "mmm d, yyyy") +
      " to " +
      dateFormat(new Date(), "mmm d, yyyy");
  const defaultFiltrationState = {
    date: defaultDate,
    showBreakdown: false,
  };
  const getParsedFilters = (searchString) => {
    const search = searchString !== undefined ? searchString : history.location.search;
    const urlParams = new URLSearchParams(search);
    const parsedParams = Object.fromEntries(urlParams.entries());
    const filtersParsed = parseFilterValues(parsedParams);
    const showBreakdownVal = parsedParams.showBreakdown === "true";

    return {
      ...defaultFiltrationState,
      ...filtersParsed,
      showBreakdown: showBreakdownVal,
    };
  };

  const [filtration, setFiltration] = useState(() => getParsedFilters());
  const [appliedFiltration, setAppliedFiltration] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadResults = (filters) => {
    if (!filters?.date) {
      return;
    }
    setLoading(true);
    API.post("/admin/publisher/report/summary", filters).then(
      (response) => {
        setData(response.data.data || []);
        setAppliedFiltration(filters);
        setLoading(false);
      }
    ).catch(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    const unlisten = history.listen((location) => {
      setFiltration(getParsedFilters(location.search));
    });
    return () => unlisten();
  }, [history]);

  const filtersChanged = appliedFiltration !== null &&
    JSON.stringify(filtration) !== JSON.stringify(appliedFiltration);

  const showBreakdown = !!filtration.showBreakdown;
  const showBreakdownApplied = !!appliedFiltration?.showBreakdown;

  const changeData = (name, value) => {
    const nextFiltration = { ...filtration, [name]: value };
    setFiltration(nextFiltration);
    const searchParams = new URLSearchParams();
    Object.entries(nextFiltration).forEach(([key, val]) => {
      if (val === undefined || val === null) return;
      if (Array.isArray(val)) {
        if (val.length) searchParams.set(key, val.map(String).join(","));
      } else {
        searchParams.set(key, String(val));
      }
    });
    history.push({
      pathname: history.location.pathname,
      search: searchParams.toString(),
    });
  };

  const changeNativeData = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    const nextFiltration = { ...filtration, [name]: value };
    setFiltration(nextFiltration);
    const searchParams = new URLSearchParams();
    Object.entries(nextFiltration).forEach(([key, val]) => {
      if (val === undefined || val === null) return;
      if (Array.isArray(val)) {
        if (val.length) searchParams.set(key, val.map(String).join(","));
      } else {
        searchParams.set(key, String(val));
      }
    });
    history.push({
      pathname: history.location.pathname,
      search: searchParams.toString(),
    });
  };

  useEffect(() => {
    const unlisten = history.listen((location) => {
      setFiltration(getParsedFilters(location.search));
    });
    return () => unlisten();
  }, [history]);

  const tableData = useMemo(() => {
    if (!data) return [];

    if (!showBreakdownApplied) {
      return data;
    }

    const childRows = [];
    data.forEach((parentRow) => {
      if (parentRow.breakdown && parentRow.breakdown.length > 0) {
        parentRow.breakdown.forEach((childRow) => {
          childRows.push({
            ...childRow,
            publisher: parentRow.publisher
          });
        });
      }
    });

    return childRows;
  }, [data, showBreakdownApplied]);

  const summaryTotals = useMemo(() => {
    let totalLeads = 0,
        totalMatched = 0,
        totalUnmatched = 0,
        totalFlagged = 0,
        totalDuplicate = 0,
        totalMatchedNoBuyer = 0,
        totalFailed = 0,
        totalReturned = 0,
        totalReturnedCredited = 0,
        totalLegsSold = 0,
        totalPayableLeads = 0;

    let totalTotalCents = 0,
        totalPubCents = 0;

    (Array.isArray(tableData) ? tableData : []).forEach((dataItem) => {
      totalLeads += Number(dataItem.leads) || 0;
      totalMatched += Number(dataItem.matched) || 0;
      totalUnmatched += Number(dataItem.unmatched) || 0;
      totalFlagged += Number(dataItem.flagged) || 0;
      totalDuplicate += Number(dataItem.duplicate) || 0;
      totalFailed += Number(dataItem.failed) || 0;
      totalMatchedNoBuyer += Number(dataItem.matchednobuyer) || 0;
      totalReturned += Number(dataItem.returned) || 0;
      totalReturnedCredited += Number(dataItem.returnedcredited) || 0;
      totalLegsSold += Number(dataItem.legssold) || 0;
      totalPayableLeads += Number(dataItem.payableLeads) || 0;

      const total = Number(dataItem.total) || 0;
      const pub = Number(dataItem.pub) || 0;

      totalTotalCents += Math.round(total * 100);
      totalPubCents += Math.round(pub * 100);
    });

    const totalTotal = totalTotalCents / 100;
    const totalPub = totalPubCents / 100;
    const totalProfit = (totalTotalCents - totalPubCents) / 100;

    const costPerLead = totalPayableLeads
        ? (totalPubCents / totalPayableLeads / 100).toFixed(2)
        : "0.00";

    const revPerLead = totalMatched
        ? (totalTotalCents / totalMatched / 100).toFixed(2)
        : "0.00";

    const avgTimesSold = (totalMatched + totalReturned + totalReturnedCredited)
        ? (totalLegsSold / (totalMatched + totalReturned + totalReturnedCredited)).toFixed(2)
        : "0";

    const margin = totalTotal
        ? (100 * totalProfit / totalTotal).toFixed(2)
        : "0";

    const successRate = totalLeads
        ? (100 * totalMatched / totalLeads).toFixed(2)
        : "0";

    return {
      totalLeads,
      totalMatched,
      totalMatchedNoBuyer,
      totalUnmatched,
      totalFlagged,
      totalFailed,
      totalReturned,
      totalReturnedCredited,
      totalTotal,
      totalPub,
      totalProfit,
      margin,
      successRate,
      costPerLead,
      revPerLead,
      avgTimesSold,
      totalPayableLeads,
    };
  }, [tableData]);

  const columns = useMemo(() => {
    const cols = [
      {
        id: 1,
        name: "Publisher",
        sortable: true,
        style: { overflowWrap: "break-word" },
        selector: (row) => (typeof row.publisher === 'object' ? row.publisher?.name?.toLowerCase() : ""),
        sortFunction: (rowA, rowB) => {
          const aId = rowA.publisher?.id || 0;
          const bId = rowB.publisher?.id || 0;
          return aId - bId;
        },
        cell: (row) => (
            <div style={{ overflow: "auto" }}>
              {typeof row.publisher === 'object' ? (
                  <Publisher publisher={row.publisher} />
              ) : (
                  ""
              )}
            </div>
        ),
      },
      {
        name: "Ref ID",
        selector: (row) => row.rcid,
        sortable: true,
        cell: (row) => (
            <div>
              {row.rcid === 'All' ? (
                  "All"
              ) : row.rcid === 'NOMATCH' ? (
                  "NOMATCH"
              ) : (
                  <RCID id={row.rcid} />
              )}
            </div>
        ),
      }
    ];

    if (showBreakdownApplied) {
      cols.push({
        name: "Ref ID Title",
        selector: (row) => row.rcidtitle,
        sortable: true,
        cell: (row) => <span>{row.rcidtitle || ""}</span>
      });
    }

    cols.push(
        {
          name: "Total leads",
          selector: (row) => parseInt(row.leads),
          cell: (row) => <div className="font-weight-bold">{row.leads}</div>,
          sortable: true,
        },
        {
          name: "Matched",
          selector: (row) => parseInt(row.matched),
          cell: (row) => (
              <div className={`font-weight-bold ${row.matched ? "text-success" : ""}`}>
                {row.matched}
              </div>
          ),
          sortable: true,
        },
        {
          name: "Matched No Buyer",
          selector: (row) => parseInt(row.matchednobuyer),
          cell: (row) => (
              <div className={`font-weight-bold`}>
                {row.matchednobuyer}
              </div>
          ),
          sortable: true,
        },
        {
          name: "Unmatched",
          selector: (row) => parseInt(row.unmatched),
          sortable: true,
        },
        {
          name: "Flagged",
          selector: (row) => parseInt(row.flagged),
          cell: (row) => (
              <div className={`${row.flagged ? "text-primary" : ""}`}>
                {row.flagged}
              </div>
          ),
          sortable: true,
        },
        {
          name: "Failed",
          selector: (row) => parseInt(row.failed),
          sortable: true,
        },
        {
          name: "Returned",
          selector: (row) => parseInt(row.returned),
          sortable: true,
        },
        {
          name: "Returned, credited",
          selector: (row) => parseInt(row.returnedcredited),
          sortable: true,
        },
        {
          name: "Success Rate",
          selector: (row) => parseFloat(row.successRate),
          cell: (row) => <div>{row.successRate}%</div>,
          sortable: true,
        },
        {
          name: "Total",
          selector: (row) => parseFloat(row.total),
          cell: (row) => (
              <div>
                {row.total ? (
                    <MoneyTotal money={row.total} moneyOriginal={row.totaloriginal} />
                ) : (
                    "$0"
                )}
              </div>
          ),
          sortable: true,
        },
        {
          name: "Publisher Revenue",
          selector: (row) => parseFloat(row.pub),
          cell: (row) => (
              <div>
                {row.pub ? (
                    <MoneyPub money={row.pub} moneyOriginal={row.puboriginal} />
                ) : (
                    "$0"
                )}
              </div>
          ),
          sortable: true,
        },
        {
          name: "Profit",
          selector: (row) => parseFloat(row.total) - parseFloat(row.pub),
          cell: (row) => (
              <div>
                {row.total || row.pub ? (
                    <MoneyTotal money={row.total - row.pub} />
                ) : (
                    "$0"
                )}
              </div>
          ),
          sortable: true,
        },
        {
          name: "Margin",
          selector: (row) => parseFloat(row.margin),
          cell: (row) => <div>{row.margin}%</div>,
          sortable: true,
        },
        {
          name: "Cost per lead",
          selector: (row) => parseFloat(row.costPerLead),
          cell: (row) => (
              <div>
                {row.costPerLead ? (
                    <MoneyPub money={row.costPerLead} />
                ) : (
                    "$0"
                )}
              </div>
          ),
          sortable: true,
        },
        {
          name: "Rev per lead",
          selector: (row) => parseFloat(row.revPerLead),
          cell: (row) => (
              <div>
                {row.revPerLead ? (
                    <MoneyTotal money={row.revPerLead} />
                ) : (
                    "$0"
                )}
              </div>
          ),
          sortable: true,
        },
        {
          name: "Avg Times Sold",
          selector: (row) => parseFloat(row.avgTimesSold),
          cell: (row) => <div>{row.avgTimesSold}</div>,
          sortable: true,
        }
    );

    return cols;
  }, [showBreakdownApplied]);

  return (
      <Container fluid className="p-0">
        {filtersChanged && (
          <style>{`
            @keyframes show-results-pulse {
              0%, 100% { box-shadow: 0 0 12px 2px rgba(0, 123, 255, 0.5); }
              50% { box-shadow: 0 0 20px 5px rgba(0, 123, 255, 0.85); }
            }
          `}</style>
        )}
        <div className="row justify-content-between align-items-center mb-3">
          <div className="col-auto">
            <h1 className="h3 mb-0">Publisher summary report</h1>
          </div>
          <div className="col-auto d-flex align-items-center">
            {!loading && (
              <>
                <div className="custom-control custom-switch mr-4">
                  <input
                      type="checkbox"
                      className="custom-control-input"
                      id="breakdownSwitch"
                      checked={showBreakdown}
                      onChange={(e) => changeData("showBreakdown", e.target.checked)}
                      style={{ cursor: "pointer" }}
                  />
                  <label
                      className="custom-control-label font-weight-bold"
                      htmlFor="breakdownSwitch"
                      style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    Show Breakdown
                  </label>
                </div>
                <CSVDownload
                    url={showBreakdown
                        ? "/admin/publisher/report/breakdownreportcsv"
                        : "/admin/publisher/report/summaryreportcsv"
                    }
                    filename={showBreakdown ? "PublisherBreakdownReport" : "PublisherSummaryReport"}
                    filtration={filtration}
                />
              </>
            )}
          </div>
        </div>
        {loading ? (
          <Loader />
        ) : (
          <>
            <Row form className={"mb-2 align-items-center"}>
              <Col md={3}>
                <DateRange
                    onChange={changeData}
                    name={"date"}
                    value={filtration?.date}
                />
              </Col>
              <Col md={2}>
                <PublisherSelect
                    name={"publisher"}
                    onChange={changeData}
                    value={filtration?.publisher}
                />
              </Col>
              <Col md={2}>
                <ProductMulti
                    name={"product"}
                    onChange={changeData}
                    value={filtration?.product}
                />
              </Col>
              <Col md={2}>
                <LeadSourceSelect
                    name={"source"}
                    onChange={changeData}
                    value={filtration?.source}
                />
              </Col>
              <Col md={3}>
                <AdminSelect
                    name={"accountmanager"}
                    onChange={changeNativeData}
                    required={false}
                    value={filtration?.accountmanager}
                    placeholder={"Account manager..."}
                />
              </Col>
            </Row>
            <div className="mb-3 text-center mt-4">
              <Button
                  color="primary"
                  onClick={() => loadResults(filtration)}
                  style={filtersChanged ? {
                    boxShadow: "0 0 16px 3px rgba(0, 123, 255, 0.7)",
                    animation: "show-results-pulse 1.5s ease-in-out infinite",
                  } : undefined}
              >
                Show results
              </Button>
            </div>
          </>
        )}
        {!loading && data && data.length > 0 ? (
            <>
              <Card className="flex-fill w-100">
                <div className="table-responsive">
                  <SortableTable columns={columns} data={tableData} pagination={false} />
                </div>
              </Card>
              <Card className="flex-fill w-100 p-3">
                <h4>Summary:</h4>
                <ul className="bull list-unstyled mb-0">
                  <li>
                    Leads: <strong>{summaryTotals.totalLeads}</strong>
                  </li>
                  <li>
                    Matched: <strong>{summaryTotals.totalMatched}</strong>
                  </li>
                  <li>
                    Matched No Buyer: <strong>{summaryTotals.totalMatchedNoBuyer}</strong>
                  </li>
                  <li>
                    Unmatched: <strong>{summaryTotals.totalUnmatched}</strong>
                  </li>
                  <li>
                    Flagged: <strong>{summaryTotals.totalFlagged}</strong>
                  </li>
                  <li>
                    Failed: <strong>{summaryTotals.totalFailed}</strong>
                  </li>
                  <li>
                    Returned: <strong>{summaryTotals.totalReturned}</strong>
                  </li>
                  <li>
                    Returned, credited: <strong>{summaryTotals.totalReturnedCredited}</strong>
                  </li>
                  <li>
                    Revenue: {summaryTotals.totalTotal ? <MoneyTotal money={summaryTotals.totalTotal} /> : "$0"}
                  </li>
                  <li>
                    Publisher revenue:{" "}
                    {summaryTotals.totalPub ? <MoneyPub money={summaryTotals.totalPub} /> : "$0"}
                  </li>
                  <li>
                    Profit:{" "}
                    {summaryTotals.totalProfit ? <MoneyTotal money={summaryTotals.totalProfit} /> : "$0"}
                  </li>
                  <li>
                    Margin:{" "}
                    {summaryTotals.margin}
                    %
                  </li>
                  <li>
                    Success rate:{" "}
                    {summaryTotals.successRate}
                    %
                  </li>
                  <li>
                    Cost per lead:{" "}
                    {summaryTotals.totalPayableLeads
                        ? <MoneyPub money={summaryTotals.costPerLead} />
                        : "$0"}
                  </li>
                  <li>
                    Rev per lead:{" "}
                    {summaryTotals.totalMatched
                        ? <MoneyTotal money={summaryTotals.revPerLead} />
                        : "$0"}
                  </li>
                  <li>
                    Avg Times Sold:{" "}
                    {summaryTotals.avgTimesSold}
                  </li>
                </ul>
              </Card>
            </>
        ) : (
            !loading && (
                <CardBody className="text-center">
                  {appliedFiltration === null ? (
                    <h5>Click &quot;Show results&quot; to load data</h5>
                  ) : (
                    <h5>Data not found</h5>
                  )}
                </CardBody>
            )
        )}
      </Container>
  );
};

export default SummaryReport;
